import { useEffect, useRef, useState } from 'preact/compat';

import { readSessionToken, SESSION_TOKEN_HEADER } from '../sessionToken';
import { StreamingEvent } from '../types/chat.types';
import { Message, WireChatMessage } from '../types/message.types';

const _rawApiEndpoint = import.meta.env.CIRCUIT_API_ENDPOINT ?? '';
const API_ENDPOINT = _rawApiEndpoint === 'undefined' ? '' : _rawApiEndpoint;

const DEFAULT_GREETING = '👋 Hello! How can I help you today?';

export function makeBotTextMessage(text: string): Message {
  return {
    sender: 'bot',
    events: [{ event: 'text', data: text } as StreamingEvent<'text'>],
  };
}

export function makeWelcomeMessage(greeting?: string): Message {
  return makeBotTextMessage(greeting || DEFAULT_GREETING);
}

function convertWireMessage(wire: WireChatMessage): Message {
  if (wire.role === 'user') {
    return { sender: 'user', text: wire.content };
  }

  let events: StreamingEvent[];
  try {
    events = JSON.parse(wire.event_stream);
    if (!Array.isArray(events) || events.length === 0) {
      events = [{ event: 'text', data: wire.content || '' }];
    }
  } catch {
    events = [{ event: 'text', data: wire.content || '' }];
  }
  return { sender: 'bot', events };
}

interface UseChatHistoryOptions {
  apiKey: string;
  isOpen: boolean;
  setMessages: (value: Message[] | ((prev: Message[]) => Message[])) => void;
  greeting?: string;
}

export function useChatHistory({
  apiKey,
  isOpen,
  setMessages,
  greeting,
}: UseChatHistoryOptions) {
  const historyLoadedRef = useRef(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  useEffect(() => {
    if (!isOpen || historyLoadedRef.current) return;
    historyLoadedRef.current = true;
    setIsLoadingHistory(true);

    const loadHistory = async () => {
      try {
        const sessionToken = readSessionToken(apiKey);
        const headers: Record<string, string> = {
          Authorization: `Bearer ${apiKey}`,
          Accept: 'application/json',
        };
        if (sessionToken) {
          headers[SESSION_TOKEN_HEADER] = sessionToken;
        }

        const response = await fetch(`${API_ENDPOINT}/chat-bot/history`, {
          method: 'GET',
          credentials: 'include',
          headers,
        });

        if (response.ok) {
          const data = await response.json();
          const history: Message[] = (data.messages ?? []).map(
            (msg: WireChatMessage) => convertWireMessage(msg),
          );
          if (history.length > 0) {
            setMessages((prev) =>
              prev.length > 0 ? [...history, ...prev] : history,
            );
            return;
          }
        }
      } catch {
        // No history available — fall through to welcome message
      }

      setMessages((prev) =>
        prev.length > 0 ? prev : [makeWelcomeMessage(greeting)],
      );
    };

    loadHistory().finally(() => setIsLoadingHistory(false));
  }, [isOpen, apiKey]);

  return { isLoadingHistory };
}
